import { useEffect, useRef, useState } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function toFullUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

function detectFileType(name) {
  const lower = String(name || '').toLowerCase()
  if (lower.endsWith('.pdf')) return 'pdf'
  if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'docx'
  if (lower.match(/\.(png|jpg|jpeg|webp|gif|svg)$/)) return 'image'
  return 'other'
}

function fileTypeChip(type) {
  if (type === 'pdf') return { label: 'PDF', bg: 'bg-error-container text-error', icon: 'picture_as_pdf' }
  if (type === 'docx') return { label: 'Word', bg: 'bg-primary-fixed text-primary', icon: 'article' }
  if (type === 'image') return { label: 'Image', bg: 'bg-tertiary-container text-on-tertiary-container', icon: 'image' }
  return { label: 'File', bg: 'bg-surface-container-high text-on-surface-variant', icon: 'attach_file' }
}

export default function AppDocumentPreviewModal({ isOpen, onClose, fileUrl, fileName }) {
  const [status, setStatus] = useState('idle') // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState('')
  const docxContainerRef = useRef(null)

  const resolvedUrl = toFullUrl(fileUrl)
  const type = detectFileType(fileName)
  const chip = fileTypeChip(type)

  // Render DOCX when modal opens
  useEffect(() => {
    if (!isOpen || type !== 'docx' || !resolvedUrl) return

    let cancelled = false
    setStatus('loading')
    setErrorMsg('')

    async function renderDocx() {
      try {
        const res = await fetch(resolvedUrl)
        if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`)
        const arrayBuffer = await res.arrayBuffer()
        if (cancelled) return

        const { renderAsync } = await import('docx-preview')
        if (cancelled) return

        if (docxContainerRef.current) {
          docxContainerRef.current.innerHTML = ''
          await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
            className: 'docx-viewer',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            useBase64URL: false,
          })
          if (!cancelled) setStatus('ready')
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(String(err?.message || 'Could not load document.'))
          setStatus('error')
        }
      }
    }

    renderDocx()
    return () => {
      cancelled = true
    }
  }, [isOpen, resolvedUrl, type])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStatus('idle')
      setErrorMsg('')
      if (docxContainerRef.current) docxContainerRef.current.innerHTML = ''
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl"
        style={{ maxHeight: '92vh' }}
      >
        {/* ── Header ── */}
        <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-outline-variant/30 bg-surface-container-low px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className={`material-symbols-outlined rounded-full p-2 text-xl ${chip.bg}`}
              style={{ fontVariationSettings: "'FILL' 1" }}>
              {chip.icon}
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-bold text-on-surface">{fileName || 'Document'}</p>
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chip.bg}`}>
                {chip.label}
              </span>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {/* Download original */}
            <a
              href={resolvedUrl}
              download={fileName || true}
              className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container px-3 py-2 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
              title="Download original file"
            >
              <span className="material-symbols-outlined text-base">download</span>
              Download
            </a>
            {/* Open in new tab (still available) */}
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container px-3 py-2 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
              title="Open in new tab"
            >
              <span className="material-symbols-outlined text-base">open_in_new</span>
            </a>
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
              aria-label="Close preview"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-surface-container-lowest">

          {/* PDF */}
          {type === 'pdf' && (
            <object
              data={resolvedUrl}
              type="application/pdf"
              className="h-full min-h-[70vh] w-full"
            >
              <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
                <span className="material-symbols-outlined text-5xl text-error">picture_as_pdf</span>
                <p className="text-sm font-medium text-on-surface-variant">
                  Your browser cannot display this PDF inline.
                </p>
                <a
                  href={resolvedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary"
                >
                  Open PDF
                </a>
              </div>
            </object>
          )}

          {/* Image */}
          {type === 'image' && (
            <div className="flex flex-1 items-center justify-center p-6">
              <img
                src={resolvedUrl}
                alt={fileName || 'Attachment'}
                className="max-h-[75vh] max-w-full rounded-xl object-contain shadow-lg"
              />
            </div>
          )}

          {/* DOCX */}
          {type === 'docx' && (
            <div className="flex min-h-0 flex-1 flex-col">
              {status === 'loading' && (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
                  <p className="text-sm font-medium text-on-surface-variant">Rendering document…</p>
                </div>
              )}
              {status === 'error' && (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
                  <span className="material-symbols-outlined text-4xl text-error">error</span>
                  <p className="text-sm font-semibold text-on-surface">Failed to load document</p>
                  <p className="text-xs text-on-surface-variant">{errorMsg}</p>
                  <a
                    href={resolvedUrl}
                    download={fileName || true}
                    className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary"
                  >
                    Download Instead
                  </a>
                </div>
              )}
              {/* docx-preview mounts content here — always rendered so the ref is stable */}
              <div
                ref={docxContainerRef}
                className="docx-preview-container overflow-auto p-4"
                style={{ display: status === 'ready' ? 'block' : 'none' }}
              />
            </div>
          )}

          {/* Other / Unsupported */}
          {type === 'other' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16 text-center">
              <span className="material-symbols-outlined text-5xl text-on-surface-variant">attach_file</span>
              <p className="text-sm font-semibold text-on-surface">Preview not available for this file type.</p>
              <p className="text-xs text-on-surface-variant">{fileName}</p>
              <a
                href={resolvedUrl}
                download={fileName || true}
                className="mt-2 rounded-lg bg-primary px-5 py-2 text-sm font-bold text-on-primary"
              >
                <span className="mr-1.5 material-symbols-outlined align-middle text-base">download</span>
                Download File
              </a>
            </div>
          )}
        </div>
      </div>

      {/* DOCX global styles injected once */}
      <style>{`
        .docx-preview-container .docx-wrapper {
          background: transparent !important;
          padding: 0 !important;
        }
        .docx-preview-container section.docx {
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          margin-bottom: 1.5rem !important;
          border-radius: 8px !important;
          background: white !important;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  )
}
